import { useCallback, useEffect, useRef, useState } from 'react'
import { animate, useMotionValue, useReducedMotion, type PanInfo } from 'framer-motion'

export const SLIDE_EASE = [0.23, 1, 0.32, 1] as const

/**
 * Index-driven slide track.
 * Mobile: one slide at the left edge, the next one peeking ~15% from the right.
 * Desktop (lg): three slides, the active one centred.
 */
export function useSlideTrack(count: number) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const [index, setIndex] = useState(0)
  const [width, setWidth] = useState(0)
  const [isDesktop, setIsDesktop] = useState(false)
  const x = useMotionValue(0)
  const reduceMotion = useReducedMotion()
  const dragged = useRef(false)
  const lastWidth = useRef(0)

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => setIsDesktop(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const gap = isDesktop ? 24 : 12
  const slideWidth = isDesktop ? Math.min(300, (width - gap * 2) / 3) : Math.max(0, width * 0.85 - gap)
  const step = slideWidth + gap

  const offsetFor = useCallback(
    (i: number) => (isDesktop ? width / 2 - slideWidth / 2 - i * step : -i * step),
    [isDesktop, width, slideWidth, step],
  )

  const settle = useCallback(
    (target: number) => {
      if (reduceMotion) {
        x.set(target)
        return undefined
      }
      return animate(x, target, { duration: 0.3, ease: SLIDE_EASE })
    },
    [reduceMotion, x],
  )

  useEffect(() => {
    const target = offsetFor(index)
    // Layout changes (resize, first measure) jump; index changes slide.
    if (lastWidth.current !== width) {
      lastWidth.current = width
      x.set(target)
      return
    }
    const controls = settle(target)
    return () => controls?.stop()
  }, [index, width, offsetFor, settle, x])

  const goTo = useCallback((i: number) => setIndex(Math.max(0, Math.min(count - 1, i))), [count])

  const onPanStart = () => {
    dragged.current = true
  }

  const onPan = (_e: PointerEvent, info: PanInfo) => {
    const atEdge = (index === 0 && info.offset.x > 0) || (index === count - 1 && info.offset.x < 0)
    x.set(offsetFor(index) + info.offset.x * (atEdge ? 0.3 : 1))
  }

  const onPanEnd = (_e: PointerEvent, info: PanInfo) => {
    const swipe = info.offset.x + info.velocity.x * 0.2
    let next = index
    if (Math.abs(swipe) > step * 0.2) next = index + (swipe < 0 ? 1 : -1)
    next = Math.max(0, Math.min(count - 1, next))
    if (next === index) settle(offsetFor(index))
    else setIndex(next)
    window.setTimeout(() => {
      dragged.current = false
    }, 0)
  }

  return {
    viewportRef,
    index,
    goTo,
    x,
    gap,
    slideWidth,
    isDesktop,
    panHandlers: { onPanStart, onPan, onPanEnd },
    wasDragged: () => dragged.current,
  }
}
